import React from "react";

import { Box, Stack, SxProps, Theme, Typography } from "@mui/material";
import { useUserRoleSelector } from "store";

import { RoleName } from "../../constants";

export interface EmptyCardImageProps {
  src: string;
  alt?: string;
  sx?: SxProps<Theme>;
}

export interface EmptyCardDescriptionProps {
  text: string;
  externalText?: string;
  sx?: SxProps<Theme>;
}

export interface Props {
  title: string;
  description: EmptyCardDescriptionProps;
  image: EmptyCardImageProps;
  fitPage?: boolean;
  actionsElement?: React.ReactNode;
  sx?: SxProps<Theme>;
  contentWidth?: number;
}

export const EmptyCard: React.FC<Props> = ({
  title,
  description,
  image,
  fitPage,
  actionsElement,
  sx,
  contentWidth
}) => {
  const role = useUserRoleSelector();
  const canPerformActions = role?.name === RoleName.Admin || role?.name === RoleName.Analyst;

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: "100%",
        flexGrow: 1,
        bgcolor: "white",
        borderRadius: "8px",
        ...(fitPage && { height: "100vh" }),
        ...(sx && sx)
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        gap="10px"
        sx={{ maxWidth: contentWidth ?? 480, textAlign: "center", p: "36px 24px" }}
      >
        <Box
          component="img"
          src={image.src}
          alt={image.alt ?? ""}
          sx={[
            {
              width: 315,
              height: 140,
              objectFit: "cover"
            },
            ...(Array.isArray(image.sx) ? image.sx : image.sx ? [image.sx] : [])
          ]}
        />

        <Typography variant="h6" sx={{ color: "#071F35", fontWeight: 500 }}>
          {title}
        </Typography>

        <Typography
          variant="body2"
          sx={[
            { color: "#496682", marginBottom: "10px" },
            ...(description.sx ? (Array.isArray(description.sx) ? description.sx : [description.sx]) : [])
          ]}
        >
          {!canPerformActions && description.externalText ? description.externalText : description.text}
        </Typography>

        {canPerformActions && actionsElement}
      </Stack>
    </Stack>
  );
};
